import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Card from "../../components/Card";
import ErrorMessage from "../../components/ErrorMessage";
import BackButton from "../../components/BackButton";
import Tag from "../../components/Tag";
import {
  createProduct,
  getProductById,
  getVendorBrands,
  getVendorSizingCharts,
  updateProduct,
} from "../../services/products";
import {
  getNormalizedVendorBrands,
  getNormalizedVendorSizingCharts,
} from "../../helpers/vendorSizingChartsList";
import {
  capitalize,
  formatNumber,
  getErrorMessage,
} from "../../helpers/functions";
import InputControl, { InputLabel } from "../../components/InputControl";
import ImageUploader, { UploadPreview } from "../../components/ImageUploader";
import toast from "react-hot-toast";
import TrashIcon from "../../assets/svg/icon_trash.svg?react";
import CautionIcon from "../../assets/svg/icon_caution.svg?react";
import { PRODUCT_FILTER_LABEL } from "../../types/types";
import ProductErrorModal from "./modals/ProductErrorModal";
import DeleteProductModal from "./modals/DeleteProductModal";
import Select, { components } from "react-select";
import ChevronDownIcon from "../../assets/svg/icon_chevron-down.svg?react";
import {
  GENDER_LABEL,
  GENDER_TYPE,
  LIST_TYPE,
  genderOptions,
  sizingCategoryOptions,
  sizingCategoryTypeOptions,
} from "../../types/optionLists";

const NEW_PARAM = "new";
export default function ProductDetails() {
  const { id } = useParams();
  const [product, setProduct] = useState();
  const [isLoading, setLoading] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [isCreatingProduct, setCreatingProduct] = useState(
    id === NEW_PARAM || false
  );
  const [productSku, setProductSku] = useState("");
  const [brandId, setBrandId] = useState("");
  const [detailBrandLabel, setDetailBrandLabel] = useState("");
  const [brandRows, setBrandRows] = useState([]);
  const [title, setTitle] = useState("");
  const [groupId, setGroupId] = useState("");
  const [color, setColor] = useState("");
  const [size, setSize] = useState("");
  const [gender, setGender] = useState();
  const [sizingCategory, setSizingCategory] = useState();
  const [sizingCategoryType, setSizingCategoryType] = useState();
  const [vendorSizingChartId, setVendorSizingChartId] = useState("");
  const [detailSizingChartLabel, setDetailSizingChartLabel] = useState("");
  const [sizingChartRows, setSizingChartRows] = useState([]);
  const [referenceDataLoading, setReferenceDataLoading] = useState(true);
  const [currentImageUrl, setCurrentImageUrl] = useState("");
  const [fileList, setFileList] = useState([]);
  const [showingProductErrorModal, setShowingProductErrorModal] =
    useState(false);
  const [isShowingDeleteConf, setShowingDeleteConf] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (id === NEW_PARAM) {
      setCreatingProduct(true);
      setVendorSizingChartId("");
      setDetailSizingChartLabel("");
      setBrandId("");
      setDetailBrandLabel("");
    } else {
      setCreatingProduct(false);
      getProductDetails();
    }
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    setReferenceDataLoading(true);
    Promise.allSettled([getVendorSizingCharts(), getVendorBrands()]).then(
      (results) => {
        if (cancelled) return;
        const [chartOutcome, brandOutcome] = results;

        if (chartOutcome.status === "fulfilled") {
          const chartRes = chartOutcome.value;
          const rawCharts = chartRes?.data ?? chartRes;
          const list = getNormalizedVendorSizingCharts(rawCharts).filter(
            (c) => c.id != null && c.id !== ""
          );
          setSizingChartRows(list);
        } else {
          setSizingChartRows([]);
        }

        if (brandOutcome.status === "fulfilled") {
          const brandRes = brandOutcome.value;
          const rawBrands = brandRes?.data ?? brandRes;
          setBrandRows(getNormalizedVendorBrands(rawBrands));
        } else {
          setBrandRows([]);
        }

        setReferenceDataLoading(false);
      }
    );
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!product || isCreatingProduct || brandId) return;
    const nm = product.brand;
    if (typeof nm !== "string" || !nm.trim() || brandRows.length === 0) return;
    const match = brandRows.find(
      (b) => (b.name || "").toLowerCase() === nm.trim().toLowerCase()
    );
    if (match?.id) setBrandId(String(match.id));
  }, [product, brandRows, isCreatingProduct, brandId]);

  const sizingChartSelectOptions = useMemo(() => {
    const base = sizingChartRows
      .filter((c) => c.id)
      .map((c) => ({
        value: String(c.id),
        label: c.chartName || "Sizing chart",
      }));
    const idStr = vendorSizingChartId ? String(vendorSizingChartId) : "";
    const hasOrphanFromDetail =
      !isCreatingProduct &&
      idStr &&
      !base.some((o) => o.value === idStr);
    if (hasOrphanFromDetail) {
      return [
        {
          value: idStr,
          label: detailSizingChartLabel || idStr,
        },
        ...base,
      ];
    }
    return base;
  }, [
    sizingChartRows,
    vendorSizingChartId,
    isCreatingProduct,
    detailSizingChartLabel,
  ]);

  const sizingChartSelectionValid = Boolean(
    vendorSizingChartId &&
      sizingChartSelectOptions.some(
        (o) =>
          String(o.value).trim() === String(vendorSizingChartId).trim()
      )
  );

  const brandSelectOptions = useMemo(() => {
    const base = brandRows.map((b) => ({
      value: String(b.id),
      label: b.name || "Brand",
    }));
    const idStr = brandId ? String(brandId) : "";
    const hasOrphanFromDetail =
      !isCreatingProduct &&
      idStr &&
      !base.some((o) => o.value === idStr);
    if (hasOrphanFromDetail) {
      return [
        {
          value: idStr,
          label: detailBrandLabel || idStr,
        },
        ...base,
      ];
    }
    return base;
  }, [brandRows, brandId, isCreatingProduct, detailBrandLabel]);

  const brandSelectionValid = Boolean(
    brandId &&
      brandSelectOptions.some(
        (o) => String(o.value).trim() === String(brandId).trim()
      )
  );

  const updateFields = (data) => {
    setProduct(data);

    setTitle(data.title || "");
    setProductSku(data.sku || "");
    const bid =
      data.brandId ?? data.BrandId ?? data.vendorBrandId ?? "";
    setBrandId(bid ? String(bid) : "");
    setDetailBrandLabel(
      typeof data.brand === "string" ? data.brand : data.brand?.name ?? ""
    );
    setGroupId(data.itemGroupId || "");

    const attributes = data.productAttribute;
    setColor(attributes.color || "");
    setSize(attributes.size || "");

    setCurrentImageUrl(data.imageUrl);
    setSizingCategory(
      data.sizingCategory
        ? {
            label: capitalize(data.sizingCategory),
            value: data.sizingCategory.toLowerCase(),
          }
        : ""
    );
    setSizingCategoryType(
      data.sizingCategoryType
        ? {
            label: capitalize(data.sizingCategoryType),
            value: data.sizingCategoryType.toLowerCase(),
          }
        : ""
    );
    setGender(
      data.gender
        ? {
            label:
              data.gender === GENDER_TYPE.MALE
                ? GENDER_LABEL.MALE
                : GENDER_LABEL.FEMALE,
            value: data.gender.toLowerCase(),
          }
        : ""
    );

    const chartId =
      data.vendorSizingChartId ??
      data.VendorSizingChartId ??
      data.vendorSizingChart?.id ??
      "";
    setVendorSizingChartId(chartId ? String(chartId) : "");
    setDetailSizingChartLabel(
      data.vendorSizingChartName ??
        data.VendorSizingChartName ??
        data.vendorSizingChart?.chartName ??
        data.vendorSizingChart?.name ??
        ""
    );
  };

  const getProductDetails = async () => {
    setLoading(true);
    setError("");

    await getProductById({ id })
      .then((res) => {
        if (res?.data) {
          updateFields(res.data);
        }
      })
      .catch((error) => {
        const message = getErrorMessage(error);
        setError(message);
      })
      .finally(() => setLoading(false));
  };

  const saveProductDetails = async () => {
    let productData = new FormData();
    const productBody = {
      sku: productSku,
      title,
      itemGroupId: groupId,
      productImage: fileList[0] || currentImageUrl,
      sizingCategory: sizingCategory?.value,
      sizingCategoryType: sizingCategoryType?.value,
      gender: gender?.value,
      BrandId: brandId,
      VendorSizingChartId: vendorSizingChartId,
    };

    const attributes = {
      color,
      size,
    };

    for (let key in productBody) {
      productData.append(key, productBody[key]);
    }

    for (let key in attributes) {
      productData.append(`ProductAttribute.${key}`, attributes[key]);
    }

    setSaveError("");
    setSaving(true);

    if (isCreatingProduct) {
      await createProduct(productData)
        .then((res) => {
          if (res) {
            toast.success("Product created");
            navigate("/products");
          }
        })
        .catch((error) => {
          const message = getErrorMessage(error);
          setSaveError(message);
        })
        .finally(() => setSaving(false));
    } else {
      productData.append("Id", id);
      await updateProduct(productData)
        .then((res) => {
          if (res?.data) {
            updateFields(res.data);
            toast.success("Product updated");
          }
        })
        .catch((error) => {
          const message = getErrorMessage(error);
          setSaveError(message);
        })
        .finally(() => setSaving(false));
    }
  };

  function getCategoriesList(listType) {
    let list = genderOptions;
    if (listType === LIST_TYPE.SIZING_CAT) list = sizingCategoryOptions;
    if (listType === LIST_TYPE.SIZING_CAT_TYPE)
      list = sizingCategoryTypeOptions;
    return list.map((option) => {
      let label = option;
      if (option === GENDER_TYPE.MALE) label = GENDER_LABEL.MALE;
      if (option === GENDER_TYPE.FEMALE) label = GENDER_LABEL.FEMALE;
      return { value: option, label: capitalize(label) };
    });
  }

  const DropdownIndicator = ({ ...props }) => {
    return (
      <components.DropdownIndicator {...props}>
        <ChevronDownIcon className="w-4 h-4 ml-2 text-[var(--clr-text)]" />
      </components.DropdownIndicator>
    );
  };

  const handleCancelCreate = () => {
    setSaveError("");
    navigate("/products");
  };

  return (
    <div className="container">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center">
          <BackButton className="mr-2" defaultPath="/products" />
          <h1 className="page-title capitalize mb-0 mr-2">
            {isCreatingProduct
              ? "Create Product"
              : product?.productName || "Product Details"}
          </h1>
          {product && !product?.isAveaseCompatible ? (
            <button
              className="bg-av-orange-light p-1 rounded-full"
              onClick={() => setShowingProductErrorModal(true)}
              title="This product is missing required fields"
            >
              <CautionIcon className="w-6 h-6 text-orange-50" />
            </button>
          ) : (
            product && (
              <Tag status={PRODUCT_FILTER_LABEL.ACTIVE}>
                {PRODUCT_FILTER_LABEL.ACTIVE}
              </Tag>
            )
          )}
          {!product?.isAveaseCompatible && (
            <ProductErrorModal
              isOpen={showingProductErrorModal}
              data={product?.systemResponse}
              onClose={() => setShowingProductErrorModal(false)}
            />
          )}
        </div>
        <div className="flex items-center ml-2 gap-2">
          {!isCreatingProduct && (
            <button
              className="btn-outline btn-error"
              disabled={isLoading || isSaving || !product}
              onClick={() => setShowingDeleteConf(true)}
            >
              <TrashIcon className="btn-prefix-icon text-av-orange my-auto" />
              <span className="text-danger-light font-semibold my-auto">
                Delete
              </span>
            </button>
          )}
          {isCreatingProduct && (
            <button
              type="button"
              className="btn border-red-500 text-red-600 bg-white py-2.5 px-4 hover:bg-red-50 hover:text-red-700"
              disabled={isSaving}
              onClick={handleCancelCreate}
            >
              Cancel
            </button>
          )}
          <button
            className={`btn-solid ${isSaving ? "loading" : ""}`}
            disabled={
              !title ||
              !productSku ||
              !sizingCategory ||
              !sizingCategoryType ||
              !brandSelectionValid ||
              !gender ||
              !sizingChartSelectionValid ||
              (referenceDataLoading && isCreatingProduct) ||
              (fileList.length === 0 && !currentImageUrl)
            }
            onClick={saveProductDetails}
          >
            {isSaving
              ? "Loading..."
              : isCreatingProduct
                ? "Create Product"
                : "Save"}
          </button>
        </div>
        {!isCreatingProduct && (
          <DeleteProductModal
            isOpen={isShowingDeleteConf}
            product={product}
            onClose={() => setShowingDeleteConf(false)}
            onSuccess={() => navigate("/products")}
          />
        )}
      </div>

      {!isLoading && error && <ErrorMessage message={error} className="mb-4" />}

      {!isSaving && saveError && (
        <ErrorMessage message={saveError} className="mb-4" />
      )}

      {!isLoading && (product || isCreatingProduct) && (
        <div className="grid md:grid-cols-[2fr_1fr] gap-10">
          <Card className="flex flex-col gap-4">
            <h2 className="page-subtitle">Details</h2>
            <div className="grid gap-4">
              <p className="font-medium text-lg">Product Identification</p>
              <div className="flex flex-col">
                <InputLabel
                  label="Product Brand"
                  id="product-brand"
                  isRequired
                />
                <Select
                  inputId="product-brand"
                  classNamePrefix="react-select"
                  className="custom-select"
                  placeholder={
                    referenceDataLoading
                      ? "Loading brands..."
                      : brandSelectOptions.length === 0
                        ? "Create a brand under Sizing Charts first"
                        : "Select brand"
                  }
                  isSearchable
                  isDisabled={
                    referenceDataLoading || brandSelectOptions.length === 0
                  }
                  options={brandSelectOptions}
                  value={
                    brandSelectOptions.find(
                      (o) =>
                        String(o.value).trim() === String(brandId).trim()
                    ) || null
                  }
                  onChange={(opt) =>
                    setBrandId(opt?.value ? String(opt.value) : "")
                  }
                  components={{ DropdownIndicator }}
                />
              </div>
              <InputControl
                label="Product Title"
                name="product-title"
                placeholder="Title"
                isRequired
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                }}
              />
              <InputControl
                label="Product SKU"
                name="product-sku"
                placeholder="Product SKU"
                isRequired
                value={productSku}
                onChange={(e) => {
                  setProductSku(e.target.value);
                }}
              />
              <div className="flex flex-col">
                <InputLabel
                  label="Sizing Chart"
                  id="product-sizing-chart"
                  isRequired
                />
                <p className="text-sm text-slate-600 mb-1">Select the sizing chart to use when providing a fit recommendation for this product. A sizing chart is automatically selected on bulk import based on the product's details.</p>
                <Select
                  id="product-sizing-chart"
                  classNamePrefix="react-select"
                  className="custom-select"
                  placeholder={
                    referenceDataLoading
                      ? "Loading sizing charts..."
                      : sizingChartSelectOptions.length === 0
                        ? "Create a sizing chart first"
                        : "Select sizing chart"
                  }
                  isSearchable
                  isDisabled={
                    referenceDataLoading ||
                    sizingChartSelectOptions.length === 0
                  }
                  options={sizingChartSelectOptions}
                  value={
                    sizingChartSelectOptions.find(
                      (o) =>
                        String(o.value).trim() ===
                        String(vendorSizingChartId).trim()
                    ) || null
                  }
                  onChange={(opt) =>
                    setVendorSizingChartId(opt?.value ? String(opt.value) : "")
                  }
                  components={{ DropdownIndicator }}
                />
              </div>

              <InputControl
                label="Product Group ID"
                name="group-id"
                placeholder="Group ID"
                value={groupId}
                onChange={(e) => {
                  setGroupId(e.target.value);
                }}
              />
              <InputControl
                label="Color"
                name="product-color"
                placeholder="Color"
                value={color}
                onChange={(e) => {
                  setColor(e.target.value);
                }}
              />
              <InputControl
                label="Size"
                name="product-size"
                placeholder="Size"
                value={size}
                onChange={(e) => {
                  setSize(e.target.value);
                }}
              />
            </div>
            <hr />
            <div className="flex flex-col gap-4">
              <p className="font-medium text-lg">Product Sizing Details</p>
              <div className="flex flex-col">
                <InputLabel
                  label="Product Sizing Category"
                  id="product-sizing-cat"
                  isRequired
                />
                <Select
                  id="product-sizing-cat"
                  classNamePrefix="react-select"
                  className="custom-select"
                  placeholder="Select sizing category"
                  isSearchable={false}
                  options={getCategoriesList(LIST_TYPE.SIZING_CAT)}
                  value={sizingCategory || ""}
                  onChange={(data) => setSizingCategory(data)}
                  components={{ DropdownIndicator }}
                />
              </div>
              <div className="flex flex-col">
                <InputLabel
                  label="Product Sizing Type"
                  id="product-sizing-type"
                  isRequired
                />
                <p className="text-sm text-slate-600 mb-1">Whether the product has long or short sleeves. If no sleeves, whether the product is long or short.</p>
                <Select
                  id="product-sizing-type"
                  classNamePrefix="react-select"
                  className="custom-select"
                  placeholder="Select sizing type"
                  isSearchable={false}
                  options={getCategoriesList(LIST_TYPE.SIZING_CAT_TYPE)}
                  value={sizingCategoryType || ""}
                  onChange={(data) => setSizingCategoryType(data)}
                  components={{ DropdownIndicator }}
                />
              </div>
              <div className="flex flex-col">
                <InputLabel
                  label="Product Intended Gender"
                  id="product-intended-gender"
                  isRequired
                />
                <Select
                  id="product-intended-gender"
                  classNamePrefix="react-select"
                  className="custom-select"
                  placeholder="Select intended gender"
                  isSearchable={false}
                  options={getCategoriesList(LIST_TYPE.GENDER)}
                  value={gender || ""}
                  onChange={(data) => setGender(data)}
                  components={{ DropdownIndicator }}
                />
              </div>
            </div>

            <hr />

            <div className="flex">
              <p className="font-medium text-lg">Image</p>
              <span className="text-red-500 ml-1">*</span>
            </div>
            {currentImageUrl ? (
              <UploadPreview
                preview={currentImageUrl}
                onClose={() => setCurrentImageUrl("")}
              />
            ) : (
              <ImageUploader
                fileList={fileList}
                radiusStyle="rounded-md"
                onChange={(files) => setFileList(files)}
                maxFiles={1}
              />
            )}
          </Card>

          {!isCreatingProduct && (
            <Card className="flex flex-col gap-4 mb-auto">
              <h2 className="page-subtitle">Analytics</h2>
              <div>
                <h3>Try ons</h3>
                <p>{formatNumber(product?.tryOns)}</p>
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
